// QBRInsightsMonthlyTable.jsx
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Colours from "../helpers/colours";
import {
    COMPARISON_FILTER,
    COMPARISON_PREVIOUS_YEAR,
    DISPLAY_ELEMENT_HELPER,
    getMetricName,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    process_,
} from "../helpers/filters";
import {
    applyTargetsToEntries,
    getComparisonMetaUi,
    TARGET_OFF,
    TARGET_MONTH,
} from "../helpers/targets";

import TooltipComparison from "./shared/TooltipComparison";
import SmallPercentage from "./SmallPercentage";
import InfoIcon from "./InfoIcon";

dayjs.extend(utc);

const METRICS = [
    "revenueTotal",
    "revenueRoom",
    "revenueRoomAndExtras",
    "bookingsRooms",
    "bookingsTotal",
    "sessionsTotal",
    "bookingEngineSessions",
    "bookingEngineSessionsRate",
    "perSessionValue",
    "perBESessionValue",
    "websiteConversionRate",
    "BEConversionRate",
];
const METRIC_COLUMN_WIDTH = 180;
const TYPE_COLUMN_WIDTH = 90;
const EXCEL_GROUP_SHADED_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF5F5F5" },
};

const buildMonthKeys = (fromDay, toDay) => {
    const start = dayjs.utc(fromDay).startOf("month");
    const end = dayjs.utc(toDay).startOf("month");

    if (!start.isValid() || !end.isValid() || end.isBefore(start, "month")) {
        return [];
    }

    const out = [];
    let cursor = start;

    while (!cursor.isAfter(end, "month")) {
        out.push(cursor.format("YYYY-MM"));
        cursor = cursor.add(1, "month");
    }

    return out;
};

const uniqueBy = (items, getKey) =>
    items.filter(
        (item, index, arr) =>
            arr.findIndex((current) => getKey(current) === getKey(item)) ===
            index,
    );

const buildMonthlyJs = ({ js, fromDay, toDay }) => {
    const targetFilter = js?.filters?.Target;
    const monthlyTargetMode =
        targetFilter === TARGET_OFF ? TARGET_OFF : TARGET_MONTH;

    return {
        ...js,
        fromDay,
        toDay,
        groupBy: [GROUP_DATE_YEAR, GROUP_DATE_MONTH],
        filters: {
            ...(js.filters ?? {}),
            Comparison: js.filters?.Comparison ?? COMPARISON_PREVIOUS_YEAR,
            Target: monthlyTargetMode,
            TargetMode: monthlyTargetMode,
        },
    };
};

const getTargetRowLabel = (targetRows) =>
    targetRows.some((row) => row.meta?.isTarget) ? "Target" : "Comparison";

const buildQBRInsightsExcelCell = (value, style = null) => ({
    value,
    style,
});

const buildQBRInsightsExcelRow = ({
    label,
    values,
    shaded = false,
    labelBold = false,
    labelItalic = false,
    labelAlign = "left",
}) => {
    const baseFill = shaded ? EXCEL_GROUP_SHADED_FILL : null;

    return [
        buildQBRInsightsExcelCell(label, {
            fill: baseFill,
            font: {
                bold: labelBold,
                italic: labelItalic,
            },
            alignment: {
                horizontal: labelAlign,
            },
        }),
        ...values.map((value) => {
            if (
                value &&
                typeof value === "object" &&
                !Array.isArray(value) &&
                (Object.prototype.hasOwnProperty.call(value, "value") ||
                    Object.prototype.hasOwnProperty.call(value, "style"))
            ) {
                return buildQBRInsightsExcelCell(value.value, {
                    ...(value.style ?? {}),
                    fill: value.style?.fill ?? baseFill,
                });
            }

            return buildQBRInsightsExcelCell(value, {
                fill: baseFill,
            });
        }),
    ];
};

const buildQBRInsightsDeltaCell = ({ actual, comparison, metric }) => {
    if (
        actual === null ||
        actual === undefined ||
        comparison === null ||
        comparison === undefined ||
        Number(comparison) === 0
    ) {
        return "—";
    }

    const percentage = Math.round(
        ((Number(actual) - Number(comparison)) / Number(comparison)) * 100,
    );

    return {
        value: `${percentage}%`,
        style: {
            trafficLight: {
                value: percentage,
                inverted: DISPLAY_ELEMENT_HELPER[metric]?.upside ?? false,
            },
        },
    };
};

export const buildQBRInsightsMonthlyTableData = ({
    js,
    fromDay,
    toDay,
    hotelID,
    dataBookings,
    dataSimple,
    dataTargets,
}) => {
    const monthKeys = buildMonthKeys(fromDay, toDay);

    if (
        monthKeys.length === 0 ||
        !["loaded", "partiallyLoaded"].includes(dataBookings?.status)
    ) {
        return {
            monthKeys,
            monthlyJs: buildMonthlyJs({ js, fromDay, toDay }),
            months: [],
        };
    }

    const monthlyJs = buildMonthlyJs({ js, fromDay, toDay });

    const { entries } = process_({
        js: monthlyJs,
        dataBookings,
        hotelID,
        dataSimple,
        dataTargets,
    });

    const entriesByMonth = Object.values(entries ?? {}).reduce((acc, entry) => {
        const fromDateGroup =
            entry?.result?.fromDateGroup ??
            entry?.resultOneYearBefore?.fromDateGroup;

        const monthKey = fromDateGroup
            ? dayjs.utc(fromDateGroup).format("YYYY-MM")
            : "";

        if (monthKey && monthKey !== "Invalid Date") {
            acc[monthKey] = entry;
        }

        return acc;
    }, {});

    const missingEntries = monthKeys.reduce((acc, monthKey) => {
        if (entriesByMonth[monthKey]) return acc;

        const monthStart = dayjs.utc(`${monthKey}-01`);

        acc[monthKey] = {
            result: {
                ...Object.fromEntries(METRICS.map((metric) => [metric, null])),
                fromDateGroup: monthStart.format("YYYY-MM-DD"),
                toDateGroup: monthStart.endOf("month").format("YYYY-MM-DD"),
            },
            resultOneYearBefore: {},
            keys: [
                [GROUP_DATE_YEAR, monthStart.format("YYYY")],
                [GROUP_DATE_MONTH, monthStart.format("MM")],
            ],
            data: [],
        };

        return acc;
    }, {});

    const targetOnlyEntries = applyTargetsToEntries({
        entries: missingEntries,
        js: monthlyJs,
        dataSimple,
        dataTargets,
        baseComparisonType: monthlyJs?.filters?.Comparison,
        processFn: (targetJs) =>
            process_({
                js: targetJs,
                dataBookings,
                hotelID,
                dataSimple,
                dataTargets,
            }),
    });

    const monthlyEntriesByMonth = { ...entriesByMonth, ...targetOnlyEntries };

    return {
        monthKeys,
        monthlyJs,
        months: monthKeys.map((monthKey) => ({
            key: monthKey,
            label: dayjs.utc(`${monthKey}-01`).format("MMM YYYY"),
            entry: monthlyEntriesByMonth[monthKey],
        })),
    };
};

export const formatQBRInsightsMetricValue = (metric, value) => {
    if (value === null || value === undefined) return "—";

    const format = DISPLAY_ELEMENT_HELPER[metric]?.format ?? {};
    const decimals = format.maxDecimals ?? 0;
    const number = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    }).format(Number(value ?? 0));

    if (format.left === "currency") return `€${number}`;
    if (format.right === "currency") return `${number} €`;
    if (format.right) return `${number}${format.right}`;

    return number;
};

export const buildQBRInsightsMonthlyTableExcelSections = ({
    js,
    hotelID,
    dataBookings,
    dataSimple,
    dataTargets,
    report,
}) => {
    const fromDay = report.fromDay ?? js.fromDay;
    const toDay = report.toDay ?? js.toDay;
    const addTotal = report.props?.addTotal ?? [
        "revenueTotal",
        "revenueRoom",
        "revenueRoomAndExtras",
        "bookingsRooms",
        "bookingsTotal",
        "sessionsTotal",
        "bookingEngineSessions",
    ];

    const { months } = buildQBRInsightsMonthlyTableData({
        js,
        fromDay,
        toDay,
        hotelID,
        dataBookings,
        dataSimple,
        dataTargets,
    });

    if (months.length === 0) return [];

    const columns = [
        "Label",
        ...months.map((month) => month.label),
        ...(addTotal.length > 0 ? ["Total"] : []),
    ];

    const rows = METRICS.flatMap((metric) => {
        const shadedGroup = METRICS.indexOf(metric) % 2 === 0;
        const targetRows = months.map((month) => ({
            month,
            meta: getComparisonMetaUi(
                month.entry?.comparisonMetaByMetric?.[metric],
            ),
        }));
        const targetRowLabel = getTargetRowLabel(targetRows);
        const actualValues = months.map(
            (month) => month.entry?.result?.[metric],
        );
        const referenceValues = months.map(
            (month) => month.entry?.resultOneYearBefore?.[metric],
        );
        const actualTotalValues = addTotal.includes(metric)
            ? actualValues.filter(
                  (value) =>
                      value !== null &&
                      value !== undefined &&
                      !Number.isNaN(Number(value)),
              )
            : [];
        const referenceTotalValues = addTotal.includes(metric)
            ? referenceValues.filter(
                  (value) =>
                      value !== null &&
                      value !== undefined &&
                      !Number.isNaN(Number(value)),
              )
            : [];
        const actualTotal =
            actualTotalValues.length > 0
                ? actualTotalValues.reduce(
                      (acc, value) => acc + Number(value),
                      0,
                  )
                : null;
        const referenceTotal =
            referenceTotalValues.length > 0
                ? referenceTotalValues.reduce(
                      (acc, value) => acc + Number(value),
                      0,
                  )
                : null;

        return [
            buildQBRInsightsExcelRow({
                label: getMetricName(metric),
                shaded: shadedGroup,
                labelBold: true,
                values: [
                    ...actualValues.map((value) =>
                        formatQBRInsightsMetricValue(metric, value),
                    ),
                    ...(addTotal.length > 0
                        ? [
                              actualTotal !== null
                                  ? formatQBRInsightsMetricValue(
                                        metric,
                                        actualTotal,
                                    )
                                  : "—",
                          ]
                        : []),
                ],
            }),
            buildQBRInsightsExcelRow({
                label: targetRowLabel,
                shaded: shadedGroup,
                labelItalic: true,
                labelAlign: "right",
                values: [
                    ...referenceValues.map((value) =>
                        formatQBRInsightsMetricValue(metric, value),
                    ),
                    ...(addTotal.length > 0
                        ? [
                              referenceTotal !== null
                                  ? formatQBRInsightsMetricValue(
                                        metric,
                                        referenceTotal,
                                    )
                                  : "—",
                          ]
                        : []),
                ],
            }),
            buildQBRInsightsExcelRow({
                label: "+/- (%)",
                shaded: shadedGroup,
                labelItalic: true,
                labelAlign: "right",
                values: [
                    ...months.map((month) =>
                        buildQBRInsightsDeltaCell({
                            actual: month.entry?.result?.[metric],
                            comparison:
                                month.entry?.resultOneYearBefore?.[metric],
                            metric,
                        }),
                    ),
                    ...(addTotal.length > 0
                        ? [
                              buildQBRInsightsDeltaCell({
                                  actual: actualTotal,
                                  comparison: referenceTotal,
                                  metric,
                              }),
                          ]
                        : []),
                ],
            }),
        ];
    });

    return [
        {
            id: "qbr-monthly-table",
            kind: "table",
            title:
                report.props?.title ??
                report.title ??
                "Monthly actual vs target",
            columns,
            rows,
        },
    ];
};

const renderTargetRowTooltip = ({ label, rows, comparisonType }) => {
    const comparisonLabel =
        COMPARISON_FILTER[comparisonType]?.label.toLowerCase() ??
        "selected comparison period";
    const targetRows = rows.filter((row) => row.meta?.isTarget);
    const comparisonRows = rows.filter((row) => row.meta && !row.meta.isTarget);

    const appliedCombos = uniqueBy(
        targetRows.map((row) => row.meta?.appliedCombo).filter(Boolean),
        (item) => item,
    );

    const periodsUsed = uniqueBy(
        targetRows.flatMap((row) => row.meta?.periodsUsed ?? []),
        (period) => `${period.validFrom}-${period.mode}-${period.percentageN}`,
    );

    const reasons = uniqueBy(
        comparisonRows.map((row) => row.meta?.reason).filter(Boolean),
        (item) => item,
    );

    return (
        <Stack spacing={1} m={2} my={1} sx={{ maxWidth: 320 }}>
            <Typography fontSize="1.1em" fontWeight="bold">
                {label}
            </Typography>

            {targetRows.length === 0 ? (
                <>
                    <Typography fontSize="0.9em">
                        No targets available for this metric. Showing comparison
                        against the {comparisonLabel} for all months.
                    </Typography>

                    {reasons.length > 0 && (
                        <Typography fontSize="0.8em" color="text.secondary">
                            Reason: {reasons.join(", ")}
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    <Typography fontSize="0.9em">
                        Target used in {targetRows.length} month
                        {targetRows.length === 1 ? "" : "s"}.
                        {comparisonRows.length > 0 &&
                            ` Comparison is against the ${comparisonLabel} in ${
                                comparisonRows.length
                            } month${comparisonRows.length === 1 ? "" : "s"}.`}
                    </Typography>

                    {appliedCombos.length > 0 && (
                        <Typography fontSize="0.85em" color="text.secondary">
                            Applied: {appliedCombos.join(", ")}
                        </Typography>
                    )}

                    {periodsUsed.length > 0 && (
                        <>
                            <Divider
                                sx={{
                                    borderColor: "rgba(0,0,0,0.08)",
                                }}
                            />

                            <Stack spacing={0.25}>
                                <Typography fontSize="0.85em">
                                    Periods used:
                                </Typography>

                                {periodsUsed.map((period) => (
                                    <Typography
                                        key={`${period.validFrom}-${period.mode}-${period.percentageN}`}
                                        fontSize="0.8em"
                                        color="text.secondary"
                                    >
                                        {period.mode} · {period.validFrom} ·{" "}
                                        {Number(period.percentageN ?? 0)}%
                                    </Typography>
                                ))}
                            </Stack>
                        </>
                    )}

                    {comparisonRows.length > 0 && (
                        <Typography fontSize="0.8em" color="text.secondary">
                            Comparison months:{" "}
                            {comparisonRows
                                .map((row) => row.month.label)
                                .join(", ")}
                        </Typography>
                    )}
                </>
            )}
        </Stack>
    );
};

const QBRInsightsMonthlyTable = ({
    title = "Monthly actual vs target",
    description,
    PDF = false,
    fromDay,
    toDay,
    addTotal = [
        "revenueTotal",
        "revenueRoom",
        "revenueRoomAndExtras",
        "bookingsRooms",
        "bookingsTotal",
        "sessionsTotal",
        "bookingEngineSessions",
    ],
}) => {
    const theme = useTheme();
    const tableOverflow = useMediaQuery(theme.breakpoints.down("md"));

    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const { dataBookings, dataSimple, dataTargets } = useSelector(
        (state) => state,
    );

    const { months, monthlyJs } = useMemo(
        () =>
            buildQBRInsightsMonthlyTableData({
                js,
                fromDay,
                toDay,
                hotelID,
                dataBookings,
                dataSimple,
                dataTargets,
            }),
        [js, fromDay, toDay, hotelID, dataBookings, dataSimple, dataTargets],
    );

    const headerStyles = {
        fontSize: "0.8em",
        backgroundColor: Colours.pillarBackgroundAttract,
        textAlign: "left",
        border: "1px solid lightgrey",
        fontWeight: "bold",
        whiteSpace: "nowrap",
    };

    const getStripedCellStyle = (rowIndex, extraStyles = {}) => ({
        border: "1px solid lightgrey",
        ...(rowIndex % 2 === 0 && {
            backgroundColor: "rgba(0, 0, 0, 0.03)",
        }),
        ...extraStyles,
    });

    if (!months.length) return null;

    return (
        <>
            <InfoIcon title={title} description={description} />

            <TableContainer
                sx={{
                    overflow: "auto",
                    maxHeight: PDF ? "none" : "800px",
                    mt: 1,
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    ...headerStyles,
                                    minWidth: METRIC_COLUMN_WIDTH,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 4,
                                    backgroundColor:
                                        Colours.pillarBackgroundAttract,
                                }}
                            >
                                Metric
                            </TableCell>

                            <TableCell
                                sx={{
                                    ...headerStyles,
                                    minWidth: TYPE_COLUMN_WIDTH,
                                    ...(tableOverflow && {
                                        position: "sticky",
                                        left: METRIC_COLUMN_WIDTH,
                                        zIndex: 3,
                                    }),
                                }}
                            >
                                Type
                            </TableCell>

                            {months.map((month) => (
                                <TableCell
                                    key={month.key}
                                    sx={{
                                        ...headerStyles,
                                        textAlign: "right",
                                        minWidth: 110,
                                    }}
                                >
                                    {month.label}
                                </TableCell>
                            ))}
                            {addTotal.length > 0 && (
                                <TableCell
                                    sx={{
                                        ...headerStyles,
                                        textAlign: "right",
                                        minWidth: 120,
                                    }}
                                >
                                    Total
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {METRICS.map((metric, metricIndex) => {
                            const targetRows = months.map((month) => ({
                                month,
                                meta: getComparisonMetaUi(
                                    month.entry?.comparisonMetaByMetric?.[
                                        metric
                                    ],
                                ),
                            }));

                            const targetRowLabel =
                                getTargetRowLabel(targetRows);

                            return (
                                <React.Fragment key={metric}>
                                    {["Actual", targetRowLabel].map(
                                        (rowLabel, rowLabelIndex) => {
                                            const rowIndex =
                                                metricIndex * 2 + rowLabelIndex;
                                            const isTargetRow =
                                                rowLabelIndex === 1;

                                            const totalValues =
                                                addTotal.includes(metric)
                                                    ? months
                                                          .map((month) =>
                                                              rowLabel ===
                                                              "Actual"
                                                                  ? month.entry
                                                                        ?.result?.[
                                                                        metric
                                                                    ]
                                                                  : month.entry
                                                                        ?.resultOneYearBefore?.[
                                                                        metric
                                                                    ],
                                                          )
                                                          .filter(
                                                              (value) =>
                                                                  value !==
                                                                      null &&
                                                                  value !==
                                                                      undefined &&
                                                                  !Number.isNaN(
                                                                      Number(
                                                                          value,
                                                                      ),
                                                                  ),
                                                          )
                                                    : [];

                                            return (
                                                <TableRow
                                                    key={`${metric}-${rowLabel}`}
                                                >
                                                    {rowLabelIndex === 0 && (
                                                        <TableCell
                                                            rowSpan={2}
                                                            sx={{
                                                                fontWeight:
                                                                    "bold",
                                                                border: "1px solid lightgrey",
                                                                verticalAlign:
                                                                    "top",
                                                                minWidth:
                                                                    METRIC_COLUMN_WIDTH,
                                                                position:
                                                                    "sticky",
                                                                left: 0,
                                                                zIndex: 2,
                                                                backgroundColor:
                                                                    Colours.plainWhite,
                                                            }}
                                                        >
                                                            {getMetricName(
                                                                metric,
                                                            )}
                                                        </TableCell>
                                                    )}

                                                    <TableCell
                                                        sx={getStripedCellStyle(
                                                            rowIndex,
                                                            {
                                                                fontWeight:
                                                                    "bold",
                                                                minWidth:
                                                                    TYPE_COLUMN_WIDTH,
                                                                ...(tableOverflow && {
                                                                    position:
                                                                        "sticky",
                                                                    left: METRIC_COLUMN_WIDTH,
                                                                    zIndex: 1,
                                                                    backgroundColor:
                                                                        rowIndex %
                                                                            2 ===
                                                                        0
                                                                            ? "rgba(0, 0, 0, 0.03)"
                                                                            : Colours.plainWhite,
                                                                }),
                                                            },
                                                        )}
                                                    >
                                                        {isTargetRow ? (
                                                            <TooltipComparison
                                                                metricName={
                                                                    metric
                                                                }
                                                                comparisonType={
                                                                    monthlyJs
                                                                        ?.filters
                                                                        ?.Comparison
                                                                }
                                                                comparisonCustom={
                                                                    monthlyJs
                                                                        ?.filters
                                                                        ?.ComparisonCustom
                                                                }
                                                                fromDay={
                                                                    fromDay
                                                                }
                                                                toDay={toDay}
                                                                value={0}
                                                                valueOld={0}
                                                                comparisonMeta={
                                                                    targetRows.find(
                                                                        (row) =>
                                                                            row.meta,
                                                                    )?.meta
                                                                }
                                                                renderContent={() =>
                                                                    renderTargetRowTooltip(
                                                                        {
                                                                            label: targetRowLabel,
                                                                            rows: targetRows,
                                                                            comparisonType:
                                                                                monthlyJs
                                                                                    ?.filters
                                                                                    ?.Comparison,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                <span
                                                                    style={{
                                                                        borderBottom:
                                                                            "1px dotted currentColor",
                                                                        cursor: "help",
                                                                    }}
                                                                >
                                                                    {rowLabel}
                                                                </span>
                                                            </TooltipComparison>
                                                        ) : (
                                                            rowLabel
                                                        )}
                                                    </TableCell>

                                                    {months.map((month) => {
                                                        const actualValue =
                                                            month.entry
                                                                ?.result?.[
                                                                metric
                                                            ];
                                                        const comparisonValue =
                                                            month.entry
                                                                ?.resultOneYearBefore?.[
                                                                metric
                                                            ];

                                                        const value =
                                                            rowLabel ===
                                                            "Actual"
                                                                ? actualValue
                                                                : comparisonValue;

                                                        const hasValuesToCompare =
                                                            !isTargetRow &&
                                                            actualValue !==
                                                                null &&
                                                            actualValue !==
                                                                undefined &&
                                                            comparisonValue !==
                                                                null &&
                                                            comparisonValue !==
                                                                undefined &&
                                                            Number(
                                                                comparisonValue,
                                                            ) > 0;

                                                        return (
                                                            <TableCell
                                                                key={`${metric}-${rowLabel}-${month.key}`}
                                                                sx={getStripedCellStyle(
                                                                    rowIndex,
                                                                    {
                                                                        textAlign:
                                                                            "right",
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                    },
                                                                )}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    {hasValuesToCompare && (
                                                                        <SmallPercentage
                                                                            brackets={
                                                                                false
                                                                            }
                                                                            tooltip={
                                                                                false
                                                                            }
                                                                            bubble={
                                                                                true
                                                                            }
                                                                            bubbleRound={
                                                                                true
                                                                            }
                                                                            actual={
                                                                                actualValue
                                                                            }
                                                                            old={
                                                                                comparisonValue
                                                                            }
                                                                            showFormula={
                                                                                false
                                                                            }
                                                                            isInverted={
                                                                                DISPLAY_ELEMENT_HELPER[
                                                                                    metric
                                                                                ]
                                                                                    ?.upside ??
                                                                                false
                                                                            }
                                                                        />
                                                                    )}

                                                                    <span
                                                                        style={{
                                                                            marginLeft:
                                                                                "auto",
                                                                        }}
                                                                    >
                                                                        {formatQBRInsightsMetricValue(
                                                                            metric,
                                                                            value,
                                                                        )}
                                                                    </span>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    })}
                                                    {addTotal.length > 0 && (
                                                        <TableCell
                                                            sx={getStripedCellStyle(
                                                                rowIndex,
                                                                {
                                                                    textAlign:
                                                                        "right",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    fontWeight:
                                                                        "bold",
                                                                },
                                                            )}
                                                        >
                                                            {totalValues.length >
                                                            0
                                                                ? formatQBRInsightsMetricValue(
                                                                      metric,
                                                                      totalValues.reduce(
                                                                          (
                                                                              acc,
                                                                              value,
                                                                          ) =>
                                                                              acc +
                                                                              Number(
                                                                                  value,
                                                                              ),
                                                                          0,
                                                                      ),
                                                                  )
                                                                : "—"}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        },
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default QBRInsightsMonthlyTable;
